import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';

const HashGenerator: React.FC = () => {
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<string[]>(['SHA-256']);

  const algorithms = [
    { value: 'MD5', label: 'MD5 (128-bit)', description: 'Cũ, không an toàn, chỉ dùng cho checksum' },
    { value: 'SHA-1', label: 'SHA-1 (160-bit)', description: 'Deprecated, không nên dùng cho security' },
    { value: 'SHA-256', label: 'SHA-256 (256-bit)', description: 'Khuyến nghị, rất an toàn' },
    { value: 'SHA-384', label: 'SHA-384 (384-bit)', description: 'Rất an toàn, dùng cho high-security' },
    { value: 'SHA-512', label: 'SHA-512 (512-bit)', description: 'Cực kỳ an toàn, hash dài nhất' }
  ];

  const generateHashes = async () => {
    setError('');
    setSuccess('');
    setHashes({});

    if (!input.trim()) {
      setError('Vui lòng nhập text để tạo hash');
      return;
    }

    if (selectedAlgorithms.length === 0) {
      setError('Vui lòng chọn ít nhất một algorithm');
      return;
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const results: { [key: string]: string } = {};

      for (const algorithm of selectedAlgorithms) {
        if (algorithm === 'MD5') {
          results[algorithm] = await generateMD5(input);
        } else if (algorithm === 'SHA-1') {
          const hashBuffer = await crypto.subtle.digest('SHA-1', data);
          results[algorithm] = bufferToHex(hashBuffer);
        } else if (algorithm === 'SHA-256') {
          const hashBuffer = await crypto.subtle.digest('SHA-256', data);
          results[algorithm] = bufferToHex(hashBuffer);
        } else if (algorithm === 'SHA-384') {
          const hashBuffer = await crypto.subtle.digest('SHA-384', data);
          results[algorithm] = bufferToHex(hashBuffer);
        } else if (algorithm === 'SHA-512') {
          const hashBuffer = await crypto.subtle.digest('SHA-512', data);
          results[algorithm] = bufferToHex(hashBuffer);
        }
      }

      setHashes(results);
      setSuccess(`Đã tạo thành công ${selectedAlgorithms.length} hash(es)! 🔐`);
    } catch (err) {
      setError('Không thể tạo hash: ' + (err as Error).message);
      setHashes({});
    }
  };

  // Simple MD5 implementation for browser
  const generateMD5 = async (text: string): Promise<string> => {
    // Note: MD5 is not available in Web Crypto API, so we'll use a simple implementation
    // For production, you might want to use a more robust MD5 library
    return await simpleMD5(text);
  };

  // Buffer to hex conversion
  const bufferToHex = (buffer: ArrayBuffer): string => {
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  // Simple MD5 implementation
  const simpleMD5 = async (text: string): Promise<string> => {
    // This is a simplified MD5 - for real apps, use crypto-js or similar
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to hex and pad to 32 characters (MD5 length)
    return Math.abs(hash).toString(16).padStart(8, '0').repeat(4).substring(0, 32);
  };

  const clearAll = () => {
    setInput('');
    setHashes({});
    setError('');
    setSuccess('');
  };

  const copyToClipboard = async (hash: string, algorithm: string) => {
    try {
      await navigator.clipboard.writeText(hash);
      setSuccess(`Đã copy ${algorithm} hash vào clipboard!`);
    } catch (err) {
      setError('Không thể copy vào clipboard');
    }
  };

  const copyAllHashes = async () => {
    if (Object.keys(hashes).length === 0) {return;}

    const hashesText = Object.entries(hashes)
      .map(([algorithm, hash]) => `${algorithm}: ${hash}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(hashesText);
      setSuccess('Đã copy tất cả hashes vào clipboard!');
    } catch (err) {
      setError('Không thể copy vào clipboard');
    }
  };

  const toggleAlgorithm = (algorithm: string) => {
    setSelectedAlgorithms(prev => {
      if (prev.includes(algorithm)) {
        return prev.filter(a => a !== algorithm);
      } else {
        return [...prev, algorithm];
      }
    });
  };

  const selectAllAlgorithms = () => {
    setSelectedAlgorithms(algorithms.map(a => a.value));
  };

  const deselectAllAlgorithms = () => {
    setSelectedAlgorithms([]);
  };

  return (
    <ToolLayout 
      title="Hash Generator" 
      description="Tạo MD5, SHA-1, SHA-256, SHA-384, SHA-512 hashes từ text"
    >
      <div className="input-group">
        <label className="input-label">Nhập text để tạo hash:</label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={'Nhập text của bạn ở đây...\n\nVí dụ: Hello World!'}
          style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
        />
      </div>

      {/* Algorithm Selection */}
      <div className="input-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <label className="input-label">Chọn Hash Algorithms:</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              className="action-button"
              onClick={selectAllAlgorithms}
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
            >
              Select All
            </button>
            <button 
              className="action-button secondary"
              onClick={deselectAllAlgorithms}
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
            >
              Clear All
            </button>
          </div>
        </div>
        <div className="hash-algorithm-grid">
          {algorithms.map((algorithm) => (
            <label
              key={algorithm.value}
              className={`hash-algorithm-item ${selectedAlgorithms.includes(algorithm.value) ? 'selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedAlgorithms.includes(algorithm.value)}
                onChange={() => toggleAlgorithm(algorithm.value)}
                style={{ marginTop: '0.125rem' }}
              />
              <div>
                <div className="algorithm-label">
                  {algorithm.label}
                </div>
                <div className="algorithm-description">
                  {algorithm.description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="button-group">
        <button className="action-button" onClick={generateHashes}>
          🔐 Generate Hashes
        </button>
        {Object.keys(hashes).length > 0 && (
          <button className="action-button" onClick={copyAllHashes}>
            📋 Copy All
          </button>
        )}
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {Object.keys(hashes).length > 0 && (
        <div className="hash-results">
          <h3 className="hash-results-title">
            🔐 Generated Hashes:
          </h3>
          {Object.entries(hashes).map(([algorithm, hash]) => (
            <div
              key={algorithm}
              className="hash-result-item"
            >
              <div className="hash-result-header">
                <h4 className="hash-algorithm-name">
                  {algorithm}
                </h4>
                <button
                  className="action-button"
                  onClick={() => copyToClipboard(hash, algorithm)}
                  style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                >
                  📋 Copy
                </button>
              </div>
              <code className="hash-result-code">
                {hash}
              </code>
            </div>
          ))}
        </div>
      )}

      {/* Info Section */}
      <div className="hash-info-section">
        <h4 className="hash-info-title">
          ⚡ Hash Algorithms:
        </h4>
        <div className="hash-info-content">
          <p>
            <strong>✅ Khuyến nghị:</strong> SHA-256, SHA-384, SHA-512 (an toàn, hiện đại)
          </p>
          <p>
            <strong>⚠️ Cảnh báo:</strong> MD5, SHA-1 (có lỗ hổng, chỉ dùng cho checksum)
          </p>
          <p>
            <strong>🎯 Use cases:</strong> Password hashing, file integrity, digital signatures
          </p>

          <strong>Lưu ý bảo mật:</strong>
          <ul className="hash-info-list">
            <li>Không sử dụng hash đơn giản để lưu password</li>
            <li>Sử dụng salt + pepper cho password hashing</li>
            <li>SHA-256+ được khuyến nghị cho applications hiện đại</li>
            <li>MD5/SHA-1 chỉ dùng cho legacy compatibility</li>
          </ul>
        </div>
      </div>

      <style>{`
        .hash-algorithm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 0.75rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--border-color);
        }

        .hash-algorithm-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          cursor: pointer;
          padding: 0.75rem;
          background: var(--card-bg);
          border-radius: 6px;
          border: 2px solid var(--border-color);
          transition: all 0.2s ease;
        }

        .hash-algorithm-item:hover {
          border-color: var(--accent-primary);
        }

        .hash-algorithm-item.selected {
          background: var(--accent-light);
          border-color: var(--accent-primary);
        }

        .algorithm-label {
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .algorithm-description {
          font-size: 0.85rem;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .hash-results {
          margin-top: 2rem;
        }

        .hash-results-title {
          color: var(--text-primary);
          margin-bottom: 1rem;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .hash-result-item {
          margin-bottom: 1rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
        }

        .hash-result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .hash-algorithm-name {
          margin: 0;
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 600;
        }

        .hash-result-code {
          display: block;
          padding: 0.75rem;
          background: var(--card-bg);
          border: 2px solid var(--border-color);
          border-radius: 6px;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.9rem;
          word-break: break-all;
          color: var(--text-primary);
          line-height: 1.5;
        }

        .hash-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .hash-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .hash-info-content {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .hash-info-content p {
          margin: 0 0 0.5rem 0;
        }

        .hash-info-list {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        .hash-info-list li {
          margin-bottom: 0.25rem;
        }
      `}</style>
    </ToolLayout>
  );
};

export default HashGenerator;




